// The shared page frame for the app's **standalone entry surfaces** — the screens a player lands
// on before there is a world to look at: live mode's Lobby (`ui/screens/Lobby.tsx`) and the
// computer-match entry (`app/rounds/RoundsRoutes.tsx`'s `VersusLiveEntry`).
//
// It exists for the same reason `ui/keyboard-policy.ts` does: two surfaces that must not drift
// apart, so the rule lives in one place rather than being copied into each. `#root` fills the
// viewport (`ui/app-shell.css`'s root-height chain), and neither of these screens sits inside a
// HUD or a canvas that would otherwise bound it — so without a frame their content stretches to
// whatever width the browser happens to be and sits flush against its edges.
//
// Deliberately NOT applied to the in-match screens (Colony, Match, Sandbox): those are full-bleed
// by design, their chrome is positioned against the viewport, and a max-width there would letterbox
// the field.
//
// **Home is also not one of these, and its narrower 600px frame is not drift.** Home is a composed
// screen rather than a bare column — an ambient colony loop behind a veil, a header scrim, and hero
// action cards — and its frame is tuned to that (600px against a 44px top padding that seats the
// hero under the scrim). Widening it to match would be a design change to a screen this module was
// never about. Two surfaces share this frame because they are the same *kind* of surface; a third
// number existing elsewhere is not by itself a reason to fold it in.

import type { CSSProperties } from 'react';

/** Comfortable measure for a single column of form controls — wide enough for the Lobby's
 * match-code row and roster cards, narrow enough that body copy stays readable on a desktop
 * monitor. Exported so a screen can compose against it without re-deriving the number. */
export const ENTRY_PAGE_MAX_WIDTH_PX = 640;

/** The frame itself: centred column, bounded measure, breathing room at the viewport edge.
 * Spread it first so a screen keeps control of its own `gap`/`flexDirection`. */
export const ENTRY_PAGE_FRAME: CSSProperties = {
  width: '100%',
  maxWidth: ENTRY_PAGE_MAX_WIDTH_PX,
  margin: '0 auto',
  padding: 'var(--sp-5) var(--sp-4)',
  boxSizing: 'border-box',
};
